import React, { useEffect, useState } from "react";
import CloseIcon from "@material-ui/icons/Close";

import {
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";
import { Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { useDispatch, useSelector } from "react-redux";
import usePermissions from "../../../hooks/usePermissions";
import { EditReviewStageName } from "../edit-review-stage-name/edit-review-stage-name.component";
import { makeKey } from "../../../utils/helpers";
import * as userStore from "../../../features/user/store/user.store";
import {
  selectPageOwnerId,
  selectPageStatus,
  selectParentStatus,
} from "../../../redux/selectors/root";
import { getReviewToggleValues } from "../../../types/review-toggle/review-toggle.functions";
import {
  changeReviewStageStatus,
  removeReviewStage,
  updateReviewStage,
} from "../../../redux/actions/reviewStagesActions";
import {
  ListActions,
  ReviewerImageWrapper,
  ReviewerImage,
  EmailCol,
} from "./review-stage-list-item.styles";
import { ReviewStageListItemProps } from "./review-stage-list-item.props";
import { canUpdateReviewStatus } from "../../../types/user/permissions/ipermissions/ipermissions.functions";

const ReviewStageListItem = ({ stage, pageType, index }: ReviewStageListItemProps) => {
  const dispatch = useDispatch();

  const userPermissions = usePermissions();

  const status = useSelector(selectPageStatus(pageType));
  const parentApprovalStatus = useSelector(selectParentStatus(pageType));

  const currentUserId = useSelector(userStore.getUserId);
  const briefOwnerId = useSelector(selectPageOwnerId(pageType));

  const { disabled, dropdownValues } = getReviewToggleValues({
    status,
    parentApprovalStatus,
    currentUserId,
    briefOwnerId,
    stage,
    canUpdateStatus: canUpdateReviewStatus(userPermissions),
  });

  const [stageStatusValue, setStageStatusValue] = useState(dropdownValues[0].value);

  useEffect(() => {
    const { dropdownValues } = getReviewToggleValues({
      status,
      parentApprovalStatus,
      currentUserId,
      briefOwnerId,
      stage,
      canUpdateStatus: canUpdateReviewStatus(userPermissions),
    });
    setStageStatusValue(dropdownValues[0].value);
  }, [parentApprovalStatus, currentUserId, briefOwnerId, stage, userPermissions, status]);

  const handleDeleteStage = () => {
    dispatch(removeReviewStage(pageType, stage.id));
  };

  const handleUpdateStageStatus = ({ target: { value: newStatus } }: React.ChangeEvent<any>) => {
    setStageStatusValue(newStatus);
    dispatch(changeReviewStageStatus(stage.id, newStatus, pageType));
  };

  const handleSave = (name: string) => {
    const updatedStage = { ...stage, name };
    dispatch(updateReviewStage(pageType, updatedStage));
  };

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: "none",
    borderTop: isDragging ? "2px solid #00CAFF" : "none",
    borderBottom: isDragging ? "2px solid #00CAFF" : "1px solid #e9e9e9",
    backgroundColor: isDragging ? "#e0f5ff" : "inherit",
    paddingLeft: "2.5rem",
    ...draggableStyle,
  });

  return (
    <Draggable
      isDragDisabled={!userPermissions.manageReviewers}
      key={makeKey("draggable", stage.id)}
      draggableId={`${stage.id}`}
      index={index}
    >
      {(provided, snapshot) => (
        <ListItem
          key={makeKey("drag-listitem", stage.id)}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
        >
          {userPermissions.manageReviewers && (
            <ListItemIcon>
              <DragIndicatorIcon />
            </ListItemIcon>
          )}
          <ListItemText>
            <Grid container spacing={3} alignItems="center">
              <EditReviewStageName
                value={stage.name}
                saveStageName={handleSave}
                pageType={pageType}
              />
              <Grid item sm={6} md={4} lg={4}>
                <ReviewerImageWrapper>
                  <ReviewerImage
                    key={makeKey("reviewer", stage.id)}
                    title={stage.reviewer.firstName +" "+ stage.reviewer.lastName}
                    alt={stage.reviewer.firstName +" "+ stage.reviewer.lastName}
                    src={(stage.reviewer.profileImageUrl) ? stage.reviewer.profileImageUrl : "/profile.png" }
                  />
                  <span className="reviewer-name">{stage.reviewer.firstName +" "+ stage.reviewer.lastName}</span>
                </ReviewerImageWrapper>
              </Grid>
              <EmailCol item sm={6} md={4} lg={3}>
                {stage.reviewer.email}
              </EmailCol>
              <ListActions item sm={6} md={12} lg={3}>
                <Select
                  labelId="demo-customized-select-label"
                  id={`status-select-${stage.id}`}
                  disabled={disabled}
                  value={stageStatusValue}
                  onChange={handleUpdateStageStatus}
                >
                  {dropdownValues.map(({ value, label }) => (
                    <MenuItem value={value} key={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {userPermissions.manageReviewers && (
                  <IconButton
                    edge="end"
                    disabled={!userPermissions.manageReviewers}
                    onClick={handleDeleteStage}
                    title="Delete"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </ListActions>
            </Grid>
          </ListItemText>
        </ListItem>
      )}
    </Draggable>
  );
};
export default ReviewStageListItem;
